// "Define Lookup" + "Define Item Change" authoring controls for a Visual /
// Dashboard CRITERION — the same capability the transaction designer offers on
// a column, reusing its exact editors (LookupEditor / ItemChangeRuleEditor /
// ItemChangeCodeEditor). Used by both the Visual designer (per prompt criteria
// rule) and the Dashboard designer (per global criterion).

import { useState } from 'react'
import { Search, Wand2, Trash2, ListChecks, Plus } from 'lucide-react'
import LookupEditor, { type LookupPatch } from '../transaction/LookupEditor'
import ItemChangeRuleEditor from '../transaction/ItemChangeRuleEditor'
import ItemChangeCodeEditor from '../transaction/ItemChangeCodeEditor'
import {
  itemChangeSummary, lookupSummary, normalizeItemChange,
} from '@twasta/page-runtime/txn'
import type {
  ColumnDef, EditMaskValue, ItemChange, ItemChangeBusinessLogic,
  ItemChangeRestApi, ItemChangeRule, LookupFilterRules,
} from '@twasta/page-runtime/txn'

export interface CriteriaLookupItemChangeValue {
  lookup?: string
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  lookup_sql?: string
  lookup_title?: string
  lookup_sql_data_col?: string
  item_change?: ItemChange
  options?: EditMaskValue[]
}

interface Props {
  project: string
  // This criterion's param name (the item_change trigger; excluded as a target).
  triggerName: string
  // The OTHER criteria's param names — offered as {form.X} tokens + fill targets.
  otherParamNames: string[]
  value: CriteriaLookupItemChangeValue
  onLookupChange: (patch: Partial<CriteriaLookupItemChangeValue>) => void
  onItemChange: (ic: ItemChange | undefined) => void
  // Fixed dropdown (picklist) values for this criterion.
  onOptionsChange: (options: EditMaskValue[] | undefined) => void
  // Longest lookup title the host stores (reports: POPHELP.MSG_TITLE).
  lookupTitleMaxLength?: number
}

const linkBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13,
  padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)',
  color: 'var(--color-text)',
}
const summaryStyle: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

export default function CriteriaLookupItemChange({
  project, triggerName, otherParamNames, value,
  onLookupChange, onItemChange, onOptionsChange, lookupTitleMaxLength,
}: Props) {
  const [editing, setEditing] = useState<'none' | 'lookup' | 'rule' | 'code'>('none')

  const options = value.options || []
  // Always write the array (empty included) so a cleared dropdown is PRESENT-
  // but-empty on disk — the save-merge treats a present key as intentional and
  // never restores a stale picker over it.
  const setOptions = (next: EditMaskValue[]) => onOptionsChange(next)

  const ic = normalizeItemChange(value.item_change ?? null)
  const hasLookup = !!((value.lookup || '').trim() || (value.lookup_sql || '').trim())
  const lkSummary = lookupSummary({
    lookup: value.lookup || '', lookup_sql: value.lookup_sql,
    lookup_filter: value.lookup_filter, lookup_filter_rules: value.lookup_filter_rules,
  })
  const icSummary = itemChangeSummary(ic)

  // Seed the LookupEditor with a minimal column built from the criterion.
  const lookupCol = {
    name: triggerName, heading: triggerName,
    lookup: value.lookup || '',
    lookup_filter: value.lookup_filter,
    lookup_filter_rules: value.lookup_filter_rules,
    lookup_sql: value.lookup_sql,
    lookup_title: value.lookup_title,
    lookup_sql_data_col: value.lookup_sql_data_col,
  } as unknown as ColumnDef

  const openItemChange = () => {
    setEditing(ic?.mode === 'business_logic' || ic?.mode === 'rest_api' ? 'code' : 'rule')
  }

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {/* Fixed dropdown (picklist) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button" style={linkBtn}
          onClick={() => setOptions(options.length ? options : [{ data: '', display: '' }])}
        >
          <ListChecks size={11} /> {options.length ? 'Dropdown' : 'Define dropdown'}
        </button>
        {options.length > 0 && (
          <button
            type="button" title="Remove dropdown" style={{ ...linkBtn, padding: '2px 4px' }}
            onClick={() => setOptions([])}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      {options.length > 0 && (
        <div style={{ display: 'grid', gap: 3, padding: '2px 0 4px 8px' }}>
          {options.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                value={o.data} placeholder="value"
                onChange={(e) => {
                  const next = [...options]; next[i] = { ...next[i], data: e.target.value }
                  setOptions(next)
                }}
                style={{ flex: 1, minWidth: 0, fontSize: 13, padding: '3px 5px', borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
              />
              <input
                value={o.display || ''} placeholder="label (optional)"
                onChange={(e) => {
                  const next = [...options]; next[i] = { ...next[i], display: e.target.value }
                  setOptions(next)
                }}
                style={{ flex: 1, minWidth: 0, fontSize: 13, padding: '3px 5px', borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
              />
              <button
                type="button" title="Remove value" style={{ ...linkBtn, padding: '2px 4px' }}
                onClick={() => setOptions(options.filter((_, j) => j !== i))}
              >
                <Trash2 size={10} />
              </button>
            </div>
          ))}
          <button
            type="button" style={{ ...linkBtn, justifySelf: 'start' }}
            onClick={() => setOptions([...options, { data: '', display: '' }])}
          >
            <Plus size={11} /> Add value
          </button>
        </div>
      )}

      {/* Lookup */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" style={linkBtn} onClick={() => setEditing('lookup')}>
          <Search size={11} /> {hasLookup ? 'Lookup' : 'Define lookup'}
        </button>
        {hasLookup && (
          <>
            <span style={{ ...summaryStyle, flex: 1 }} title={lkSummary}>{lkSummary}</span>
            <button
              type="button" title="Remove lookup" style={{ ...linkBtn, padding: '2px 4px' }}
              onClick={() => onLookupChange({
                lookup: '', lookup_filter: '', lookup_filter_rules: undefined,
                lookup_sql: '', lookup_title: '', lookup_sql_data_col: '',
              })}
            >
              <Trash2 size={11} />
            </button>
          </>
        )}
      </div>

      {/* Item change */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" style={linkBtn} onClick={openItemChange}>
          <Wand2 size={11} /> {ic ? 'Item change' : 'Define item change'}
        </button>
        {ic && (
          <>
            <span style={{ ...summaryStyle, flex: 1 }} title={icSummary}>{icSummary}</span>
            <button
              type="button" title="Remove item change" style={{ ...linkBtn, padding: '2px 4px' }}
              onClick={() => onItemChange(undefined)}
            >
              <Trash2 size={11} />
            </button>
          </>
        )}
      </div>

      {editing === 'lookup' && (
        <LookupEditor
          open
          onClose={() => setEditing('none')}
          column={lookupCol}
          formColumns={otherParamNames}
          headerColumns={[]}
          project={project}
          titleMaxLength={lookupTitleMaxLength}
          onSave={(patch: LookupPatch) => {
            onLookupChange({
              lookup: patch.lookup,
              lookup_filter: patch.lookup_filter,
              lookup_filter_rules: patch.lookup_filter_rules,
              lookup_sql: patch.lookup_sql,
              lookup_title: patch.lookup_title,
              lookup_sql_data_col: patch.lookup_sql_data_col,
            })
            setEditing('none')
          }}
        />
      )}

      {editing === 'rule' && (
        <ItemChangeRuleEditor
          open
          onClose={() => setEditing('none')}
          value={ic?.mode === 'rule' ? (ic as ItemChangeRule) : null}
          triggerColumnName={triggerName}
          formColumnNames={otherParamNames}
          headerColumnNames={[]}
          project={project}
          onSave={(rule: ItemChangeRule) => { onItemChange(rule); setEditing('none') }}
        />
      )}

      {editing === 'code' && (
        <ItemChangeCodeEditor
          open
          onClose={() => setEditing('none')}
          value={ic}
          formColumnNames={otherParamNames}
          headerColumnNames={[]}
          project={project}
          onSave={(next: ItemChangeBusinessLogic | ItemChangeRestApi) => {
            onItemChange(next); setEditing('none')
          }}
        />
      )}
    </div>
  )
}
